import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactComponent as Filter } from '../../assets/svg/filter.svg'
import { ReactComponent as DropdownTransparent } from '../../assets/svg/DropdownTransparent.svg'
import Loading from '../Loading'
import { useClickOutside } from '../../hooks/useClickOutside'

const PILL_CLASS = 'inline-flex flex-shrink-0 items-center justify-center px-3 h-10 min-w-[40px] w-fit rounded-full border border-deselectPurple whitespace-nowrap cursor-pointer transition'
const pillClass = (active) => `${PILL_CLASS} ${active ? 'bg-mudPurple text-white' : 'bg-lightBG text-mudPurple'}`

const ClassroomFilterDropdown = ({
    id = '',
    applyButtonId = '',
    title,

    filterYears,
    selectedYearFilter,
    selectYearFilter,

    selectedClassFilter,
    selectedYearList,
    selectAllClasses,
    selectClass,

    applyFilter,
    loadingFilter,

    applyStaffFilter,
    loadingStaffFilter,
    hideStaffButton,
    showResetButton = false,
    reset,

    height = 40,
    dropdownWidth = 330,
}) => {
    const { t } = useTranslation()
    const [showTargetGroup, setShowTargetGroup] = useState(false)
    const rootRef = useRef(null)
    const close = useCallback(() => setShowTargetGroup(false), [])
    useClickOutside(rootRef, close, showTargetGroup)

    return (
        <div className="flex flex-col relative noselect" ref={rootRef} onMouseLeave={close}>
            <div
                className="all-target-button bg-[#FAFAFF] z-20 hover:bg-lightBG transition"
                id={id}
                style={{ height }}
                onClick={(e) => {
                    e.preventDefault()
                    setShowTargetGroup((prev) => !prev)
                }}
                role="button"
                aria-haspopup="dialog"
                aria-expanded={showTargetGroup}
            >
                <Filter fill="#5E639B" />

                <p className="text-center">{title}</p>

                <div className={`classroom-chevron${showTargetGroup ? ' is-flipped' : ''}`}>
                    <DropdownTransparent style={{ color: '#5E639B' }} />
                </div>
            </div>

            {showTargetGroup && (
                <div className="absolute top-0 right-0 bg-lightBG rounded-[29px] z-10 pt-10 px-5 pb-5 classroom-dropdown-panel" style={{ width: dropdownWidth }}>
                    <p className="text-[16px] font-semibold text-deselectPurple">{t('Years')}</p>

                    <div className="overflow-auto flex flex-row gap-[10px] py-[10px] blue-small-scroll-horizontal ">
                        <button type="button" className={pillClass(selectedYearFilter.includes('all'))} onClick={() => selectYearFilter('all')}>
                            {t('all')}
                        </button>

                        {filterYears?.map((year) => (
                            <button type="button" key={year} className={pillClass(selectedYearFilter.includes(year))} onClick={() => selectYearFilter(year)}>
                                <p className="whitespace-nowrap">{year}</p>
                            </button>
                        ))}
                    </div>

                    <p className="mt-5 text-[16px] font-semibold text-deselectPurple">{t('classes')}</p>

                    <div className="overflow-auto flex flex-row gap-[10px] py-[10px] blue-small-scroll-horizontal">
                        <button type="button" className={pillClass(selectedClassFilter.includes('all'))} onClick={selectAllClasses}>
                            {t('all')}
                        </button>

                        {selectedYearList?.map((cls) => (
                            <button type="button" key={cls} className={pillClass(selectedClassFilter.includes(cls))} onClick={() => selectClass(cls)}>
                                <p className="whitespace-nowrap">{cls}</p>
                            </button>
                        ))}
                    </div>

                    <div className="flex flex-row gap-5 mt-5">
                        {showResetButton && (
                            <button type="button" className="flex flex-row items-center justify-center rounded-full cursor-pointer h-10 flex-1 bg-white" onClick={() => reset()}>
                                <p className="text-darkenPurple text-[16px] leading-5 font-semibold">{t('Reset')}</p>
                            </button>
                        )}
                        <button
                            type="button"
                            className="flex items-center justify-center rounded-full cursor-pointer h-10 flex-1 bg-highlightColor"
                            onClick={() => applyFilter()}
                            disabled={loadingFilter}
                            id={applyButtonId}
                        >
                            {loadingFilter ? (
                                <Loading primaryColor="#9392AD" secondaryColor="#9392AD" size={25} stroke={7} />
                            ) : (
                                <p className="text-darkenPurple text-[16px] font-semibold">{t('apply')}</p>
                            )}
                        </button>

                        {!hideStaffButton && (
                            <button
                                type="button"
                                className="flex items-center justify-center rounded-full cursor-pointer h-10 flex-1 bg-white"
                                onClick={applyStaffFilter}
                                disabled={loadingStaffFilter}
                            >
                                {loadingStaffFilter ? (
                                    <Loading primaryColor="#9392AD" secondaryColor="#9392AD" size={25} stroke={7} />
                                ) : (
                                    <p className="text-darkenPurple text-[16px] font-semibold">{t('staff')}</p>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default React.memo(ClassroomFilterDropdown)
